import axios from "axios"
import React, { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import Swal from "sweetalert2"
import AtsLogo from "../../src/assets/images/ats-logo.png"
import UsAtsImg2 from "../../src/assets/images/USATS2.png"
import { FaCheck, FaCheckCircle } from "react-icons/fa"
import { Modal } from "react-bootstrap"

const defaultJobData = {
  company: "Bharat ATS",
  title: "Job Opportunity",
  companyDescription: "",
  recruiter: "Recruitment Team",
  location: "Not specified",
  experience: "Not specified",
  type: "Full-time",
  about: ["We would like to discuss this opportunity with you."],
  skills: [],
  responsibilities: [],
}

const decodeSkills = value => {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
  } catch {
    return value
      .split(",")
      .map(skill => skill.trim())
      .filter(Boolean)
  }
}

const convertTo12Hour = time => {
  const [h, m, s] = time.split(":")
  let hours = parseInt(h, 10)
  const ampm = hours >= 12 ? "PM" : "AM"
  hours = hours % 12 || 12
  const seconds = s ? `:${s}` : ""
  return `${hours}:${m}${seconds} ${ampm}`
}

const stripHtml = (value = "") =>
  value
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim()

const splitDescription = (value = "") => {
  const text = stripHtml(value)
  if (!text) return defaultJobData.about
  return text
    .split(/(?<=[.?!])\s+/)
    .map(item => item.trim())
    .filter(Boolean)
    .slice(0, 6)
}

const timeSlots = [
  "00:00","00:30","01:00","01:30","02:00","02:30","03:00","03:30",
  "04:00","04:30","05:00","05:30","06:00","06:30","07:00","07:30",
  "08:00","08:30","09:00","09:30","10:00","10:30","11:00","11:30",
  "12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30",
  "16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30",
  "20:00","20:30","21:00","21:30","22:00","22:30","23:00","23:30",
]

const getNearestSlot = (slots, selectedDate) => {
  const now = new Date()
  const todayStr = now.toISOString().split("T")[0]
  if (selectedDate !== todayStr) return slots[0]
  const currentMinutes = now.getHours() * 60 + now.getMinutes()
  let nearest = slots[0]
  let minDiff = Infinity
  slots.forEach(slot => {
    const [h, m] = slot.split(":").map(Number)
    const slotMinutes = h * 60 + m
    const diff = slotMinutes - currentMinutes
    if (diff >= 0 && diff < minDiff) {
      minDiff = diff
      nearest = slot
    }
  })
  return nearest
}

const buildExperience = (minExp, maxExp) => {
  const min = minExp?.trim?.() || minExp
  const max = maxExp?.trim?.() || maxExp
  if (min && max) return `${min} - ${max} Years`
  if (min) return `${min}+ Years`
  if (max) return `Up to ${max} Years`
  return defaultJobData.experience
}

const parseConsentScheduleData = search => {
  const params = new URLSearchParams(search)
  return {
    jobId: params.get("job_id") || "",
    consentToken: params.get("consent_token") || "",
    company: params.get("company_name") || params.get("client_name") || defaultJobData.company,
    title: params.get("job_title") || defaultJobData.title,
    companyDescription: stripHtml(params.get("company_description") || params.get("company_decsription") || params.get("description") || "") || defaultJobData.companyDescription,
    recruiter: params.get("recruiter_name") || defaultJobData.recruiter,
    location: params.get("location") || defaultJobData.location,
    experience: params.get("experience") || defaultJobData.experience,
    type: params.get("job_type") || defaultJobData.type,
    about: splitDescription(params.get("jd") || params.get("job_description") || ""),
    skills: decodeSkills(params.get("skills")),
    responsibilities: splitDescription(params.get("roles_responsibilities") || ""),
  }
}

const normalizeConsentJobData = (payload, fallback = {}) => {
  const locationValue = Array.isArray(payload?.location)
    ? payload.location.join(", ")
    : payload?.location || fallback.location || defaultJobData.location
  const recruiterValue = Array.isArray(payload?.recruiters)
    ? payload.recruiters.join(", ")
    : Array.isArray(payload?.recruiters_names)
    ? payload.recruiters_names.join(", ")
    : payload?.recruiter_name || fallback.recruiter || defaultJobData.recruiter
  const jobType = payload?.workplace_type || payload?.job_type || payload?.employment_type || payload?.type || fallback.type || defaultJobData.type
  const skillsValue = Array.isArray(payload?.skills) ? payload.skills.filter(Boolean) : decodeSkills(payload?.skills)
  return {
    jobId: payload?.job_id || payload?.id || fallback.jobId || "",
    consentToken: fallback.consentToken || "",
    company: payload?.company_name || payload?.client_name || fallback.company || defaultJobData.company,
    title: payload?.job_title || fallback.title || defaultJobData.title,
    companyDescription: stripHtml(payload?.company_description || payload?.company_decsription || payload?.description || fallback.companyDescription || "") || defaultJobData.companyDescription,
    recruiter: recruiterValue,
    location: locationValue,
    company_logo: payload?.company_logo || "",
    experience: buildExperience(payload?.min_exp, payload?.max_exp),
    type: jobType,
    about: splitDescription(payload?.job_description || payload?.jd || ""),
    responsibilities: splitDescription(payload?.roles_responsibility || ""),
    skills: skillsValue,
  }
}

const buildScheduledAt = (date, time) => {
  const secondsTime = time.length === 5 ? `${time}:00` : time
  const localDate = new Date(`${date}T${secondsTime}`)
  const offsetMinutes = -localDate.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? "+" : "-"
  const absOffset = Math.abs(offsetMinutes)
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0")
  const offsetMins = String(absOffset % 60).padStart(2, "0")
  return `${date}T${secondsTime}${sign}${offsetHours}:${offsetMins}`
}

const getConsentSwalOptions = (type, responseData) => {
  const fallbackOptions = {
    icon: type === "yes" ? "success" : "info",
    title: type === "yes" ? "Thank You!" : "Consent Declined",
    text: type === "yes" ? "You have given consent successfully." : "You have declined consent.",
    confirmButtonText: "OK",
    timer: 3000,
    timerProgressBar: true,
  }
  const responseHtml = typeof responseData === "string" ? responseData : responseData?.html || responseData?.message_html || null
  if (typeof responseHtml === "string" && /<[^>]+>/.test(responseHtml)) {
    return { icon: type === "yes" ? "success" : "info", html: responseHtml, confirmButtonText: "OK", timer: 3000, timerProgressBar: true }
  }
  return fallbackOptions
}

/* ─── Responsive inline styles ─── */


function ConsentSchedule() {
  const location = useLocation()
  const { token1 } = useParams()
  const [step, setStep] = useState("main")
  const [prefDate, setPrefDate] = useState("")
  const [prefTime, setPrefTime] = useState("")
  const [slotError, setSlotError] = useState(false)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [consentLoadingType, setConsentLoadingType] = useState(null)
  const [declineReason, setDeclineReason] = useState("")
  const [declineError, setDeclineError] = useState(false)
  const [activeTab, setActiveTab] = useState("jd")
  const userData = JSON.parse(sessionStorage.getItem("user-info")) || {}
  const companies = userData?.user_details?.companies_data || []
  const selectedTimeRef = React.useRef(null)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const hasVgus = companies.some(c => c.company_domain === "vgus")
  const logo = hasVgus ? UsAtsImg2 : AtsLogo

  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date()
    return { year: d.getFullYear(), month: d.getMonth() }
  })

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay()
    return day === 0 ? 6 : day - 1
  }

  const [jobData, setJobData] = useState(() => parseConsentScheduleData(location.search))
  const [jobLoading, setJobLoading] = useState(Boolean(parseConsentScheduleData(location.search).jobId))
  const [canSubmit, setCanSubmit] = useState(null)

  useEffect(() => {
    if (step === "schedule" && prefTime) {
      const timer = setTimeout(() => {
        selectedTimeRef.current?.scrollIntoView({ block: "center" })
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [prefTime, step])

  useEffect(() => {
    const checkConsentToken = async () => {
      const consentToken = token1 || jobData.consentToken
      if (!consentToken) { setCanSubmit(false); return }
      try {
        const response = await axios.get(`/api/v1/check-consent-token/?token=${consentToken}`)
        setCanSubmit(response.data?.can_submit !== false)
      } catch {
        setCanSubmit(false)
      }
    }
    checkConsentToken()
  }, [token1, jobData.consentToken])

  useEffect(() => {
    if (prefDate) {
      const nearest = getNearestSlot(timeSlots, prefDate)
      setPrefTime(nearest)
    }
  }, [prefDate])

  useEffect(() => {
    let isMounted = true
    const initialData = parseConsentScheduleData(location.search)
    const fetchConsentJobData = async () => {
      if (!initialData.jobId) { setJobLoading(false); return }
      setJobLoading(true)
      try {
        const response = await axios.get(`/api/v1/consent-job-data/${initialData.jobId}/`)
        const payload = response?.data?.data || response?.data || {}
        if (isMounted) { setJobData(normalizeConsentJobData(payload, initialData)); setJobLoading(false) }
      } catch {
        if (isMounted) { setJobData(prev => ({ ...prev, ...initialData })); setJobLoading(false) }
      }
    }
    fetchConsentJobData()
    return () => { isMounted = false }
  }, [location.search])

  useEffect(() => {
    const today = new Date()
    let day = today.getDay()
    if (day === 0) today.setDate(today.getDate() + 1)
    if (day === 6) today.setDate(today.getDate() + 2)
    setPrefDate(today.toISOString().split("T")[0])
  }, [])

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search)
      const nextParams = new URLSearchParams()
      if (params.get("job_id")) nextParams.set("job_id", params.get("job_id"))
      if (params.get("consent_token")) nextParams.set("consent_token", params.get("consent_token"))
      const nextQuery = nextParams.toString()
      window.history.replaceState(window.history.state, "", `${location.pathname}${nextQuery ? `?${nextQuery}` : ""}`)
    }
  }, [location.pathname, location.search])

  const handleConfirmSlot = async () => {
    if (!prefDate && !prefTime) { setSlotError("both"); return }
    if (!prefDate) { setSlotError("date"); return }
    if (!prefTime) { setSlotError("time"); return }
    const consentToken = token1 || jobData.consentToken
    if (!consentToken) {
      Swal.fire({ icon: "error", title: "Invalid Link", text: "This schedule link is missing the required token." })
      return
    }
    setSlotError(false)
    setScheduleLoading(true)
    try {
      await axios.post(`/api/v1/update-consent/${consentToken}/?response=yes`)
      await axios.post(`/api/v1/schedule-consent-call/${consentToken}/`, { scheduled_at: buildScheduledAt(prefDate, prefTime) })
      setStep("confirmed")
      Swal.fire({ icon: "success", title: "Interview Scheduled", text: "Your interview slot has been scheduled successfully.", timer: 2500, showConfirmButton: false })
    } catch (error) {
      Swal.fire({ icon: "error", title: "Scheduling Failed", text: error?.response?.data?.message || error?.response?.data?.error || "Unable to schedule the interview." })
    } finally {
      setScheduleLoading(false)
    }
  }

  if (canSubmit === null) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f4f8" }}>
        <div style={{ textAlign: "center", color: "#103d63", fontSize: 15 }}>Validating...</div>
      </div>
    )
  }

  if (canSubmit === false) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", background: "#f0f4f8" }}>
        <div style={{ width: "100%", maxWidth: 480, background: "#fff", borderRadius: 20, padding: "48px 28px", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <FaCheckCircle style={{ fontSize: 44, color: "#103d63", marginBottom: 12 }} />
          <h2 style={{ color: "#103d63", fontWeight: 700, marginBottom: 10, fontSize: 22 }}>Already Responded</h2>
          <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.6 }}>Thanks! You've already responded to this consent request. An agent will contact you at your selected time slot.</p>
        </div>
      </div>
    )
  }

  return (
    <>

      <div className="cs-root">
        <div className="cs-card">
          <div className="cs-header">
            <div className="cs-kicker">Interview Invitation</div>
          </div>

          <div className="cs-body">
            <div className="cs-section">
              <img
                  src={jobData.company_logo}
                  alt="company-logo"
                  style={{
                    width: 200,
                    height: 55,
                    borderRadius: 8,
                    objectFit: "contain",
                    border: "1px solid #e5e7eb",
                    padding: 4,
                    background: "#fff",
                  }}
                />
            </div>
            <div className="cs-section">
              <div className="cs-section-label">Company Name</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#103d63" }}>{jobData.company}</div>
            </div>

            {jobData.companyDescription && (
              <div className="cs-section">
                <div className="cs-section-label">Company Description</div>
                <p className="cs-about-text" style={{ color: "#103d63" }}>{jobData.companyDescription}</p>
              </div>
            )}

            <p className="cs-about-text" style={{ marginBottom: 12, fontWeight: 600, color: "#0f172a", fontSize: 15 }}>
              Job Title : {jobData.title}
            </p>

            <div className="cs-tabs">
              <button className={activeTab === "jd" ? "active" : ""} onClick={() => setActiveTab("jd")}>Job Description</button>
              <button className={activeTab === "resp" ? "active" : ""} onClick={() => setActiveTab("resp")}>Responsibilities</button>
            </div>

            <div className="cs-section">
              <div className="cs-section-label">
                {activeTab === "jd" ? "Job Description" : "Responsibilities"}
              </div>
              {activeTab === "jd" && jobData.about.map((item, index) => (
                <p key={index} className="cs-about-text">{item}</p>
              ))}
              {activeTab === "resp" && (
                jobData.responsibilities?.length
                  ? jobData.responsibilities.map((item, index) => <p key={index} className="cs-about-text">{item}</p>)
                  : <p className="cs-about-text">No responsibilities provided.</p>
              )}
            </div>

            <div className="cs-info-grid">
              <div className="cs-info-box">
                <div className="cs-info-label">Location</div>
                <div className="cs-info-value">{jobData.location}</div>
              </div>
              <div className="cs-info-box">
                <div className="cs-info-label">Experience</div>
                <div className="cs-info-value">{jobData.experience}</div>
              </div>
              <div className="cs-info-box">
                <div className="cs-info-label">Employment Type</div>
                <div className="cs-info-value">{jobData.type}</div>
              </div>
              <div className="cs-info-box">
                <div className="cs-info-label">Recruiter</div>
                <div className="cs-info-value">{jobData.recruiter}</div>
              </div>
            </div>

            {!!jobData.skills.length && (
              <div className="cs-section">
                <div className="cs-section-label">Required Skills</div>
                <div className="cs-skills-list">
                  {jobData.skills.map(skill => (
                    <span key={skill} className="cs-skill-chip">{skill}</span>
                  ))}
                </div>
              </div>
            )}

            <hr className="cs-divider" />

            {step === "main" && (
              <>
                <div className="cs-cta-title">Are you interested in this opportunity?</div>
                <div className="cs-cta-sub">Pick a response below.</div>
              </>
            )}

            {step !== "confirmed" && step !== "declined" && (
              <>
                <button
                  className="cs-btn-interested"
                  onClick={e => {
                    setStep(prev => prev === "schedule" ? "main" : "schedule")
                    e.stopPropagation()
                  }}
                >
                  <div className="cs-btn-icon green">Y</div>
                  <div>
                    <div className="cs-btn-label">Yes, I'm Interested</div>
                    <div className="cs-btn-sublabel">Accept and choose an interview slot</div>
                  </div>
                  <div className="cs-btn-arrow">›</div>
                </button>

                <button
                  className="cs-btn-decline"
                  onClick={() => setShowDeclineModal(true)}
                  disabled={scheduleLoading}
                >
                  <div className="cs-btn-icon red">N</div>
                  <div>
                    <div className="cs-btn-label">Not Interested</div>
                    <div className="cs-btn-sublabel">Tell us why</div>
                  </div>
                </button>
              </>
            )}

            {/* Decline Modal */}
            <Modal
              show={showDeclineModal}
              onHide={() => { setShowDeclineModal(false); setDeclineReason(""); setDeclineError(false) }}
              centered
              size="md"
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>Reason for Not Interested</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <label className="form-label">Please tell us why <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={declineReason}
                  onChange={e => {
                    setDeclineReason(e.target.value.replace(/[^a-zA-Z0-9 .,!?-]/g, ""))
                    setDeclineError(false)
                  }}
                  placeholder="Enter your reason..."
                  style={{ fontSize: 14 }}
                />
                {declineError && <div className="text-danger mt-1" style={{ fontSize: 13 }}>Please enter a reason.</div>}
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowDeclineModal(false); setDeclineReason(""); setDeclineError(false) }}>Cancel</button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={async () => {
                    if (!declineReason.trim()) { setDeclineError(true); return }
                    const consentToken = token1 || jobData.consentToken
                    try {
                      await axios.post(`/api/v1/update-consent/${consentToken}/`, { response: "no", reason: declineReason })
                      setShowDeclineModal(false)
                      setStep("declined")
                      Swal.fire({ icon: "info", title: "Response Submitted", text: "Your response has been recorded." })
                    } catch {
                      Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" })
                    }
                  }}
                >Submit</button>
              </Modal.Footer>
            </Modal>

            {step === "declined" && (
              <div className="cs-declined-box">
                <div className="cs-state-title">Response Recorded</div>
                <div className="cs-state-sub">Thank you for letting us know. We will keep your profile for future opportunities.</div>
              </div>
            )}

            {step === "confirmed" && (
              <div className="cs-confirmed-box">
                <div className="cs-state-title">Interview Scheduled</div>
                <div className="cs-state-sub">
                  Your slot has been selected for <strong>{prefDate}</strong> at <strong>{convertTo12Hour(prefTime)}</strong>.
                </div>
              </div>
            )}

            <div className="cs-privacy">Your response is confidential and shared only with the hiring team.</div>
          </div>
        </div>

        <div className="cs-footer">Powered by Bharat ATS</div>
      </div>

      {/* ── Schedule Modal ── */}
      <Modal
        show={step === "schedule"}
        onHide={() => { setStep("main"); setSlotError(false) }}
        centered
        dialogClassName="sched-modal"
        className="sched-modal"
      >
        <Modal.Body style={{ padding: 0, borderRadius: 16, overflow: "hidden" }}>
          {/* Header */}
          <div className="sched-header">
            <div className="sched-avatar">{jobData.company?.charAt(0) || "B"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sched-company">{jobData.company}</div>
              <div className="sched-title">{jobData.title}</div>
            </div>
            <button
              className="sched-close"
              onClick={() => { setStep("main"); setSlotError(false) }}
            >×</button>
          </div>

          {/* Subheader */}
          <div className="sched-subheader">Select a Date &amp; Time</div>

          {/* Body */}
          <div className="sched-body">
            {/* Calendar */}
            <div className="sched-calendar">
              <div className="cal-nav">
                <button
                  className="cal-nav-btn"
                  onClick={() => setCalMonth(prev => {
                    const d = new Date(prev.year, prev.month - 1)
                    return { year: d.getFullYear(), month: d.getMonth() }
                  })}
                >‹</button>
                <span className="cal-month-label">
                  {new Date(calMonth.year, calMonth.month).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                </span>
                <button
                  className="cal-nav-btn"
                  onClick={() => setCalMonth(prev => {
                    const d = new Date(prev.year, prev.month + 1)
                    return { year: d.getFullYear(), month: d.getMonth() }
                  })}
                >›</button>
              </div>

              <div className="cal-days-header">
                {["MON","TUE","WED","THU","FRI","SAT","SUN"].map(d => (
                  <div key={d} className="cal-day-name">{d}</div>
                ))}
              </div>

              <div className="cal-days-grid">
                {Array.from({ length: getFirstDayOfMonth(calMonth.year, calMonth.month) }).map((_, i) => (
                  <div key={`e-${i}`} />
                ))}
                {Array.from({ length: getDaysInMonth(calMonth.year, calMonth.month) }, (_, i) => i + 1).map(day => {
                  const dateStr = `${calMonth.year}-${String(calMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
                  const today = new Date(); today.setHours(0, 0, 0, 0)
                  const thisDate = new Date(calMonth.year, calMonth.month, day)
                  const isSelected = prefDate === dateStr
                  const isToday = thisDate.getTime() === today.getTime()
                  return (
                    <button
                      key={day}
                      className="cal-day-btn"
                      onClick={() => { setPrefDate(dateStr); setPrefTime(""); setSlotError(false) }}
                      style={{
                        border: isToday && !isSelected ? "2px solid #0069ff" : "2px solid transparent",
                        background: isSelected ? "#0069ff" : "transparent",
                        color: isSelected ? "#fff" : "#111827",
                        fontWeight: isSelected || isToday ? 700 : 400,
                        cursor: "pointer",
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f3f4f6" }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? "#0069ff" : "transparent" }}
                    >
                      {day}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Dividers */}
            <div className="sched-divider-v" />
            <div className="sched-divider-h" />

            {/* Time Slots */}
            <div className="sched-slots">
              {prefDate ? (
                <>
                  <div className="sched-slots-label">
                    {new Date(prefDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
                  </div>
                  <div className="sched-slots-grid">
                    {timeSlots.map(time => {
                      const isSelected = prefTime === time
                      return (
                        <button
                          key={time}
                          className="sched-slot-btn"
                          ref={isSelected ? selectedTimeRef : null}
                          onClick={() => { setPrefTime(time); setSlotError(false) }}
                          style={{
                            border: isSelected ? "2px solid #0069ff" : "1px solid #e5e7eb",
                            background: isSelected ? "#0069ff" : "#fff",
                            color: isSelected ? "#fff" : "#111827",
                            fontWeight: isSelected ? 600 : 400,
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = "#0069ff" }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = "#e5e7eb" }}
                        >
                          {convertTo12Hour(`${time}:00`)}
                        </button>
                      )
                    })}
                  </div>
                </>
              ) : (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14, gap: 8, minHeight: 160, textAlign: "center" }}>
                  <span style={{ fontSize: 30 }}>📅</span>
                  <span>Pick a date to see<br />available times</span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sched-footer">
            {slotError && (
              <div className="sched-error">
                ⚠️
                {slotError === "both" && "Please select both a date and a time slot."}
                {slotError === "date" && "Please select a date."}
                {slotError === "time" && "Please select a time slot."}
              </div>
            )}
            <div className="sched-footer-btns">
              <button className="sched-btn-cancel" onClick={() => { setStep("main"); setSlotError(false) }}>Cancel</button>
              <button className="sched-btn-confirm" onClick={handleConfirmSlot} disabled={scheduleLoading}>
                {scheduleLoading ? "Scheduling..." : "Confirm Interview"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ConsentSchedule